import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import { toast } from "react-toastify";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import DeleteModal from "components/common/DeleteModal";
import StatusBadge from "components/common/StatusBadge";
import { IndexColumn } from "components/common/columns";
import { DateOnly } from "components/common/Date";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  ComplaintMaterialSelectionType,
  ComplaintMaterialStatus,
  ComplaintMaterialWarrantyStatus,
  complaintMaterialStatusMap,
} from "features/ComplaintMaterials/constants/complaint-material.constants";
import { ComplaintMaterial } from "features/ComplaintMaterials/types/complaint-material.types";
import {
  deleteComplaintMaterial,
  getComplaintMaterials,
} from "features/ComplaintMaterials/services/ComplaintMaterialService";
import ComplaintMaterialListFilters from "./ComplaintMaterialListFilters";

const ComplaintMaterialList = () => {
  document.title = getDocumentTitle("Complaint Materials");
  const { can } = useAccess();
  const canCreate = can(Permissions.COMPLAINT_MATERIAL.CREATE);
  const canUpdate = can(Permissions.COMPLAINT_MATERIAL.UPDATE);
  const canDelete = can(Permissions.COMPLAINT_MATERIAL.DELETE);
  const canRead = can(Permissions.COMPLAINT_MATERIAL.READ);

  const tableRef = useRef<() => void>(null);
  const [selected, setSelected] = useState<ComplaintMaterial | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [status, setStatus] = useState<ComplaintMaterialStatus | "">("");
  const [clientId, setClientId] = useState<number | null>(null);
  const [clientOption, setClientOption] = useState<
    { value: number; label: string } | undefined
  >(undefined);
  const [warrantyStatus, setWarrantyStatus] = useState<
    ComplaintMaterialWarrantyStatus | ""
  >("");

  const requestParams = useMemo(
    () => ({
      status: status || undefined,
      clientId: clientId ?? undefined,
      warrantyStatus: warrantyStatus || undefined,
    }),
    [status, clientId, warrantyStatus],
  );

  const filterCount = [status, clientId, warrantyStatus].filter(
    (v) => v != null && v !== "",
  ).length;

  const clearFilters = useCallback(() => {
    setStatus("");
    setClientId(null);
    setClientOption(undefined);
    setWarrantyStatus("");
  }, []);

  const onClickDelete = useCallback((record: ComplaintMaterial) => {
    setSelected(record);
    setDeleteOpen(true);
  }, []);

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await deleteComplaintMaterial(selected.id);
      if (res.success) {
        toast.success(res.message);
        tableRef.current?.();
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? "Could not delete record.",
      );
    } finally {
      setDeleteOpen(false);
      setSelected(null);
    }
  };

  const columns: ColumnDef<ComplaintMaterial>[] = useMemo(
    () => [
      IndexColumn<ComplaintMaterial>(),
      {
        header: "Item",
        id: "item",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const label =
            r.selectionType === ComplaintMaterialSelectionType.MATERIAL
              ? (r.material?.name ?? "—")
              : (r.name?.trim() || "—");
          return (
            <div className="d-flex flex-column">
              <span className="fw-medium">{label}</span>
              {r.serialNo ? (
                <span className="text-muted small">S/N {r.serialNo}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        header: "Client",
        id: "client",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => row.original.client?.name ?? "—",
      },
      {
        header: "Complaint Date",
        accessorKey: "complaintReceivedDate",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DateOnly date={row.original.complaintReceivedDate} />
        ),
      },
      {
        header: "Warranty",
        id: "warrantyStatus",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.warrantyStatus ===
              ComplaintMaterialWarrantyStatus.IN_WARRANTY
                ? "bg-success-subtle text-success"
                : "bg-secondary-subtle text-secondary"
            }`}
          >
            {row.original.warrantyStatus ===
            ComplaintMaterialWarrantyStatus.IN_WARRANTY
              ? "In warranty"
              : "Out of warranty"}
          </span>
        ),
      },
      {
        header: "Status",
        id: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <StatusBadge value={row.original.status} map={complaintMaterialStatusMap} />
        ),
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const showEdit = canUpdate;
          const hasMenuItems = canRead || showEdit || canDelete;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Complaint material actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canRead ? (
                  <DropdownItem tag={Link} to={`/complaint-materials/${r.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {showEdit ? (
                  <DropdownItem
                    tag={Link}
                    to={`/complaint-materials/edit/${r.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canDelete ? (
                  <>
                    {canRead || showEdit ? <DropdownItem divider /> : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickDelete(r)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canRead, canUpdate, canDelete, onClickDelete],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteOpen}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        recordId={
          (selected?.selectionType === ComplaintMaterialSelectionType.MATERIAL
            ? selected?.material?.name
            : selected?.name) ?? undefined
        }
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Complaint Materials" pageTitle="Clients" />
          <Card>
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
              <div>
                <h5 className="card-title mb-1">Complaint Materials</h5>
                <p className="text-muted small mb-0">
                  Faulty materials/equipment clients send back for repair —
                  from intake through inspection, resolution, dispatch, and
                  hand-back.
                </p>
              </div>
              {canCreate ? (
                <Link
                  to="/complaint-materials/create"
                  className="btn btn-primary"
                >
                  <i className="ri-add-line align-bottom me-1" />
                  Add
                </Link>
              ) : null}
            </CardHeader>
            <div className="card-body pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getComplaintMaterials}
                requestParams={requestParams}
                pageSize={10}
                globalSearch
                SearchPlaceholder="Search by description, serial no, or item…"
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-0"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                filterCount={filterCount}
                onFiltersClear={clearFilters}
                toolbarRight={
                  <ComplaintMaterialListFilters
                    status={status}
                    onStatusChange={setStatus}
                    clientId={clientId}
                    clientOption={clientOption}
                    onClientChange={(id, opt) => {
                      setClientId(id);
                      setClientOption(opt);
                    }}
                    warrantyStatus={warrantyStatus}
                    onWarrantyStatusChange={setWarrantyStatus}
                  />
                }
              />
            </div>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ComplaintMaterialList;
